/**
 * USB QR 리더기 + 네이티브 키오스크 QR 이벤트 통합 훅
 * docs/tools_all.md §11-2
 *
 * - document keydown 버퍼링 (200ms 휴지 → 버퍼 리셋, Enter → flush)
 * - window 'nativeQRScan' 커스텀 이벤트 수신(브릿지앱 dispatchKeyEvent 네이티브 주입)
 * - enabled=false 일 때 두 채널 모두 무시
 *
 * ⚠️ 한글 IME 대응(2026-07-24): 크롬 등 브라우저에서 한글 IME 가 켜져 있으면 USB 리더가
 *    보내는 영문자가 keydown 단계에서 IME 에 먹혀 `e.key` 가 'Process'/조합중 한글로 바뀌고
 *    keyCode 가 229 로 온다. 예전엔 `e.key.length === 1` 만 봐서 이 글자들이 통째로 유실 →
 *    스캔 원문이 깨져 "유효하지 않은 QR" 이 났다(브릿지앱은 네이티브가 unicodeChar 로 수집해 면역).
 *    → 아래 `resolveScanChar` 가 IME 오염 시 물리 키(`e.code`, US-QWERTY 에뮬레이트)로 복원한다.
 */

import { useEffect, useRef } from 'react';

// US-QWERTY 물리 키(e.code) → [무-shift, shift] 문자.
// QR 스캐너는 US 배열 키보드를 에뮬레이트하므로, e.code(물리 위치, IME·OS 레이아웃 무관)로
// 원문을 복원할 수 있다. e.key 는 IME/레이아웃에 오염되지만 e.code 는 오염되지 않는다.
const CODE_CHAR = (() => {
  const map = {};
  for (let i = 0; i < 26; i += 1) {
    const lower = String.fromCharCode(97 + i);
    map[`Key${lower.toUpperCase()}`] = [lower, lower.toUpperCase()];
  }
  const shiftDigits = ')!@#$%^&*('; // index = 숫자값
  for (let d = 0; d < 10; d += 1) map[`Digit${d}`] = [String(d), shiftDigits[d]];
  Object.assign(map, {
    Minus: ['-', '_'], Equal: ['=', '+'],
    BracketLeft: ['[', '{'], BracketRight: [']', '}'], Backslash: ['\\', '|'],
    Semicolon: [';', ':'], Quote: ["'", '"'], Backquote: ['`', '~'],
    Comma: [',', '<'], Period: ['.', '>'], Slash: ['/', '?'],
    Space: [' ', ' '],
  });
  return map;
})();

/**
 * keydown 이벤트에서 스캔 문자 1개를 뽑는다(IME 면역).
 *  1) IME 미개입 + 단일 인쇄가능 문자 → `e.key` 그대로(모든 문자·레이아웃 커버).
 *  2) IME 개입(isComposing 또는 keyCode 229) → 물리 키(`e.code`)로 US-QWERTY 문자 복원.
 * 수식키/매핑 없는 키는 null(오수집 방지).
 *
 * @param {{key:string, code:string, shiftKey:boolean, isComposing:boolean, keyCode:number}} e
 * @returns {string|null} 수집할 문자 또는 null
 */
export function resolveScanChar(e) {
  const composing = e.isComposing || e.keyCode === 229;
  if (!composing && typeof e.key === 'string' && e.key.length === 1) {
    return e.key;
  }
  const pair = CODE_CHAR[e.code];
  if (pair) return e.shiftKey ? pair[1] : pair[0];
  return null;
}

// 폼 입력(INPUT/TEXTAREA/SELECT/contenteditable) 포커스 중에는 스캔으로 오수집하지 않는다.
// (브릿지앱 KioskActivity dispatchKeyEvent 주석이 전제하는 "웹 핸들러의 포커스 가드".)
const isEditableTarget = (t) =>
  !!t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.tagName === 'SELECT' || t.isContentEditable === true);

const BUFFER_RESET_MS = 200; // 키 간 휴지 초과 시 버퍼 리셋(스캐너 버스트 vs 수동입력 구분)

export default function useUsbQrReader({ enabled = true, onScan, minLength = 4 }) {
  const bufferRef = useRef('');
  const timerRef = useRef(null);
  // onScan 최신 참조 유지 — effect 의존성에서 빼서 리스너 재등록 회피
  const scanRef = useRef(onScan);
  useEffect(() => { scanRef.current = onScan; }, [onScan]);

  useEffect(() => {
    if (!enabled) return undefined;

    const flush = () => {
      const code = bufferRef.current.trim();
      bufferRef.current = '';
      if (timerRef.current) { clearTimeout(timerRef.current); timerRef.current = null; }
      if (code.length >= minLength && typeof scanRef.current === 'function') {
        scanRef.current(code, 'usb');
      }
    };

    const handleKeyDown = (e) => {
      // 폼 입력 포커스 중이면 무시(수동 타이핑을 스캔으로 오인 방지)
      if (isEditableTarget(e.target)) return;
      // Enter → flush. IME 확정 Enter 도 잡도록 e.code 로도 본다(물리 Enter 키).
      if (e.key === 'Enter' || e.code === 'Enter' || e.code === 'NumpadEnter') {
        e.preventDefault();
        flush();
        return;
      }
      const ch = resolveScanChar(e);
      if (ch) {
        bufferRef.current += ch;
        if (timerRef.current) clearTimeout(timerRef.current);
        timerRef.current = setTimeout(() => { bufferRef.current = ''; }, BUFFER_RESET_MS);
      }
    };

    const handleNativeScan = (e) => {
      const code = (e.detail || '').trim();
      if (code.length >= minLength && typeof scanRef.current === 'function') {
        scanRef.current(code, 'native');
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    window.addEventListener('nativeQRScan', handleNativeScan);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('nativeQRScan', handleNativeScan);
      if (timerRef.current) { clearTimeout(timerRef.current); timerRef.current = null; }
      // 🔴 버퍼도 비운다 (2026-08-07). 안 비우면 Enter 전에 비활성화된 미완성 조각이 남아
      //    **다음 활성화 때 앞에 붙어** 코드가 깨진다(→ "유효하지 않은 QR"). 그 조각을 지울
      //    BUFFER_RESET_MS 타이머도 바로 위에서 함께 없애므로 **스스로 회복되지 않는다.**
      //    관리자 바가 열리면 스캔을 멈추는 변경이 이 토글을 잦게 만들어 노출이 커졌다.
      bufferRef.current = '';
    };
  }, [enabled, minLength]);
}
